import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input, Select,
  Stack, Text,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Image as ChakraImage,
} from '@chakra-ui/react';
import { getBackendAssetURL, getBackendURL, getBackendURLWithSuffix, setAndClear, apiFetch } from '../../../utils/utils';
import { Link, useNavigate } from 'react-router-dom';

function AddDistributorPage() {

  let navigate = useNavigate();

  const [distributorEmail, setDistributorEmail] = useState('');
  const [distributorSelectedPlan, setDistributorSelectedPlan] = useState({});
  const [distributorAmount, setDistributorAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('OFFLINE');
  const [distributorPaymentTrackingNumber, setDistributorTrackingNumber] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [plans, setPlans] = useState([]);
  const [qrCodeImg, setQrCodeImg] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();


  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchSettings();
  }, []);

  const fetchSettings = () => {
    apiFetch(`${getBackendURL()}/admin/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.qrCodePath) {
          setQrCodeImg(data.qrCodePath.startsWith('http') ? data.qrCodePath : `${getBackendAssetURL()}/${data.qrCodePath}`);
        }
        if (data.paymentGatewayEnabled === false) {
          setPaymentGatewayEnabled(false);
          setPaymentMethod('OFFLINE');
        }
      })
      .catch(err => console.error('Error fetching settings:', err));
  };

  const fetchPlans = () => {
    apiFetch(`${getBackendURL()}/admin/distributor/plans`)
      .then(response => response.json())
      .then(response => {
        if (response !== null && response.plans.length > 0) {
          let plans = [];
          response.plans.forEach(plan => {
            plans.push({
              id: plan._id,
              name: plan.name,
              price: plan.price,
              autoSMSProfiles: plan.autoSMSProfiles,
              digiCardProfiles: plan.digiCardProfiles,
            });
          });
          setPlans(plans);
        } else {
          setPlans([]);
        }
      })
      .catch(error => {
        console.log(error);
        setPlans([]);
      });
  };

  // Removed sendPaymentLink as it will happen on onSubmit for ONLINE

  const onSubmit = () => {
    setError('');
    if (distributorEmail === ''
      || distributorSelectedPlan.name === undefined
      || distributorAmount === 0
      || (distributorPaymentTrackingNumber === '' && paymentMethod === 'OFFLINE')) {
      setError('Please set the proper details');
      return;
    }
    setInProgress(true);
    apiFetch(`${getBackendURL()}/admin/distributor/distributors/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: distributorEmail,
        planId: distributorSelectedPlan.id,
        amount: distributorAmount,
        trackingNumber: distributorPaymentTrackingNumber,
        paymentMethod: paymentMethod,
      }),
    })
      .then(response => response.json())
      .then(response => {
        setInProgress(false);
        if (response.distributor) {
          setAndClear(response.message, setSuccess);
          setTimeout(() => {
            navigate('/distributors');
          }, 1000);
        } else {
          setAndClear(response.message, setError);
        }
        setDistributorEmail('');
        setDistributorSelectedPlan({});
        setDistributorAmount(0);
        setDistributorTrackingNumber('');
      })
      .catch(error => {
        console.log(error);
        setInProgress(false);
        setAndClear(error, setError);
      });
  };

  const setSelectedPlan = (id) => {
    if (!id) {
      setDistributorSelectedPlan({});
      setDistributorAmount(0);
      return;
    }
    let plan = plans.find(plan => plan.id === id);
    if (plan) {
      setDistributorAmount(plan.price);
      setDistributorSelectedPlan(plan);
    }
  };


  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to='/'>Home</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to='/distributors'>Distributors</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to='/distributors/add'>Add</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4} direction={{ base: 'column', lg: 'row' }}>
        <Box w={'100%'}>
          <Card bg={useColorModeValue('white', 'gray.700')} data-testid='add-plan-form-card'>
            <CardHeader>
              <Heading size='md'>{'Add Distributor'}</Heading>
            </CardHeader>
            <Divider />
            <CardBody>
              <Alert status='success' display={success ? 'flex' : 'none'}>
                <AlertIcon />
                {success}
              </Alert>
              <Alert status='error' display={error ? 'flex' : 'none'}>
                <AlertIcon />
                {error}
              </Alert>
              <Stack spacing='4' mt='2'>
                <Stack spacing={2} direction={{ base: 'column', lg: 'row' }}>
                  <FormControl isRequired>
                    <FormLabel>Google Email</FormLabel>
                    <Input type='email' id='email' name='email' value={distributorEmail}
                      onChange={(event) => setDistributorEmail(event.target.value)} />
                    <FormHelperText>
                      Google Email for distributor login.
                    </FormHelperText>
                  </FormControl>
                </Stack>
                <Stack spacing={2} direction={{ base: 'column', lg: 'row' }}>
                  <FormControl isRequired>
                    <FormLabel>Plan Name</FormLabel>
                    <Select placeholder='Select Plan'
                      onChange={(event) => setSelectedPlan(event.target.value)}>
                      {plans.map(plan => {
                        return <option key={plan.id} value={plan.id}>{plan.name} (Rs.{plan.price})</option>;
                      })
                      }
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Payment Method</FormLabel>
                    <RadioGroup onChange={setPaymentMethod} value={paymentMethod}>
                      <Stack direction="row" spacing={5}>
                        <Radio value="OFFLINE">Offline Payment</Radio>
                        <Radio value="ONLINE" isDisabled={!paymentGatewayEnabled}>Online Payment</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                  {paymentMethod === 'OFFLINE' && (
                    <FormControl>
                      <FormLabel>Offline QR Code</FormLabel>
                      <Button colorScheme="blue" variant="outline" onClick={onOpen} isDisabled={distributorAmount === 0} size="sm">
                        Show QR Code
                      </Button>
                    </FormControl>
                  )}
                </Stack>
                <Stack spacing={2} direction={{ base: 'column', lg: 'row' }}>
                  <FormControl isRequired>
                    <FormLabel>Amount</FormLabel>
                    <Input type='number' id='amount' name='amount' value={distributorAmount} disabled
                      onChange={(event) => setDistributorAmount(event.target.value)} />
                    <FormHelperText>
                      {distributorSelectedPlan.price !== undefined ? `Please collect the amount of ${distributorSelectedPlan.price}` : 'Plan price'}
                    </FormHelperText>
                  </FormControl>
                  <FormControl isRequired={paymentMethod === 'OFFLINE'}>
                    <FormLabel>Payment Tracking Id</FormLabel>
                    <Input
                      type="text"
                      id="trackingId"
                      name="trackingId"
                      value={distributorPaymentTrackingNumber}
                      isDisabled={paymentMethod === 'ONLINE'}
                      placeholder={paymentMethod === 'ONLINE' ? 'Auto-generated' : 'Enter tracking ID'}
                      onChange={event =>
                        setDistributorTrackingNumber(event.target.value)
                      }
                    />
                    <FormHelperText>
                      {paymentMethod === 'ONLINE'
                        ? 'Payment link will be sent to the distributor.'
                        : 'Enter the tracking ID from the offline payment.'}
                    </FormHelperText>
                  </FormControl>
                </Stack>
                {paymentMethod === 'ONLINE' && (
                  <Box p={4} borderWidth={1} borderRadius="md" textAlign="center" bg="blue.50" borderColor="blue.200">
                    <Text fontWeight="bold" color="blue.700">Online payment link will be sent to: {distributorEmail || 'distributor email'}</Text>
                    <Text fontSize="sm" color="blue.600">The link will be generated and sent automatically upon adding the distributor.</Text>
                  </Box>
                )}
              </Stack>
            </CardBody>

            <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Scan to Pay</ModalHeader>
                <ModalCloseButton />
                <ModalBody textAlign="center">
                  <Text fontWeight="bold" mb={4}>Amount: Rs. {distributorAmount}</Text>
                  <Box p={4} bg="gray.100" borderRadius="md" display="inline-block">
                    <Box w="200px" h="200px" bg="white" display="flex" alignItems="center" justifyContent="center">
                      <ChakraImage
                        src={qrCodeImg}
                        alt="QR Code"
                        w="200px"
                        h="200px"
                      />
                    </Box>
                  </Box>
                  <Text mt={4} fontSize="sm" color="gray.500">Scan this QR code with any UPI app to complete the payment.</Text>
                </ModalBody>
                <ModalFooter>
                  <Button colorScheme="blue" mr={3} onClick={onClose}>
                    Done
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>

            <Divider />
            <CardFooter>
              <ButtonGroup spacing='2'>
                <Button variant='solid' colorScheme='teal' onClick={onSubmit} isLoading={inProgress}
                  loadingText='Submitting...'>
                  Add
                </Button>
              </ButtonGroup>
            </CardFooter>
          </Card>
        </Box>
      </Stack>
    </>
  );
}

export default AddDistributorPage;